import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

const CurrencyContext = createContext(null);

// Browser locale → currency inference (best-effort, user can always override in Settings)
const LOCALE_TO_CURRENCY = {
  en: 'USD', 'en-US': 'USD', 'en-GB': 'GBP', 'en-AU': 'AUD', 'en-CA': 'CAD', 'en-IN': 'INR',
  de: 'EUR', fr: 'EUR', es: 'EUR', it: 'EUR', nl: 'EUR', pt: 'EUR', 'pt-BR': 'BRL',
  ja: 'JPY', ko: 'KRW', zh: 'CNY', 'zh-HK': 'HKD', 'zh-SG': 'SGD',
  ar: 'AED', hi: 'INR', tr: 'TRY', ru: 'RUB', pl: 'PLN', sv: 'SEK',
};

function detectBrowserCurrency() {
  try {
    const stored = localStorage.getItem('pushpro_currency');
    if (stored) return stored;
    const langs = navigator.languages || [navigator.language || 'en'];
    for (const l of langs) {
      if (LOCALE_TO_CURRENCY[l]) return LOCALE_TO_CURRENCY[l];
      const short = l.split('-')[0];
      if (LOCALE_TO_CURRENCY[short]) return LOCALE_TO_CURRENCY[short];
    }
  } catch (_) { /* no-op */ }
  return 'EUR';
}

export function CurrencyProvider({ children }) {
  const { user } = useAuth();
  const [currency, setCurrencyState] = useState(detectBrowserCurrency);
  const [rates, setRates] = useState(null);          // { USD: 1.08, JPY: 164.0, ... }
  const [currenciesMeta, setCurrenciesMeta] = useState({});

  // Load rates once on mount (and refresh every 30 minutes)
  useEffect(() => {
    let cancelled = false;
    const loadRates = async () => {
      try {
        const res = await api.get('/currency/rates');
        if (cancelled) return;
        setRates(res.data.rates || {});
        setCurrenciesMeta(res.data.currencies || {});
      } catch (_) { /* use fallback */ }
    };
    loadRates();
    const id = setInterval(loadRates, 30 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // When a logged-in user has a saved preference, use it
  useEffect(() => {
    if (user?.preferred_currency && user.preferred_currency !== currency) {
      setCurrencyState(user.preferred_currency);
      try { localStorage.setItem('pushpro_currency', user.preferred_currency); } catch (_) {}
    }
  }, [user?.preferred_currency]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCurrency = useCallback((code) => {
    setCurrencyState(code);
    try { localStorage.setItem('pushpro_currency', code); } catch (_) {}
    const token = typeof window !== 'undefined' ? localStorage.getItem('sd_token') : null;
    if (token) {
      api.put('/me/currency', { preferred_currency: code }).catch(() => {});
    }
  }, []);

  const convert = useCallback((amountEur) => {
    const amt = Number(amountEur) || 0;
    if (!rates || currency === 'EUR') return amt;
    const r = rates[currency];
    return r ? amt * r : amt;
  }, [rates, currency]);

  const formatPrice = useCallback((amountEur, opts = {}) => {
    const target = opts.currency || currency;
    const meta = currenciesMeta[target] || { symbol: '', decimals: 2, symbol_before: true };
    const rate = target === 'EUR' ? 1 : (rates?.[target] || 1);
    const converted = (Number(amountEur) || 0) * rate;
    const rounded = meta.decimals > 0 ? converted.toFixed(meta.decimals) : Math.round(converted).toString();
    // Add thousands separators with the active UI locale
    const num = Number(rounded).toLocaleString(undefined, {
      minimumFractionDigits: meta.decimals,
      maximumFractionDigits: meta.decimals,
    });
    return meta.symbol_before ? `${meta.symbol}${num}` : `${num} ${meta.symbol.trim()}`;
  }, [currency, currenciesMeta, rates]);

  const value = useMemo(() => ({
    currency,
    setCurrency,
    rates,
    currenciesMeta,
    convert,
    formatPrice,
    ready: !!rates,
  }), [currency, setCurrency, rates, currenciesMeta, convert, formatPrice]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // Safe no-op fallback so components rendered outside the provider still work
    return {
      currency: 'EUR',
      setCurrency: () => {},
      rates: null,
      currenciesMeta: {},
      convert: (a) => a,
      formatPrice: (a) => `€${(Number(a) || 0).toFixed(2)}`,
      ready: false,
    };
  }
  return ctx;
}
